import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { I18nProvider, useI18n } from '@/components/_vs/i18n/I18nProvider';
import { ConsentProvider } from '@/components/_vs/consent/ConsentProvider';
import { CookieBanner } from '@/components/_vs/consent/CookieBanner';
import { GoogleTagManager } from '@/components/_vs/consent/GoogleTagManager';
import { BlockRenderer } from '@/components/_vs/blocks/BlockRenderer';
import { ScrollProgress } from '@/components/_vs/animations/ScrollProgress';
import { CustomCursor } from '@/components/_vs/animations/CustomCursor';
import { CommandPalette } from '@/components/_vs/nav/CommandPalette';
import { Loader } from './components/animations/Loader';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { NotFound } from './components/layout/NotFound';
import { PageMeta } from './components/layout/PageMeta';
import { LocalBusinessSchema } from './components/layout/LocalBusinessSchema';
import { TypographySettings } from './components/layout/TypographySettings';
import { BackgroundSettings } from './components/layout/BackgroundSettings';
import { startLenis, stopLenis, getLenis } from './utils/lenis';
import { ScrollTrigger } from './utils/gsap';
import { LOCALES, DEFAULT_LOCALE, localizedPath, parseLocalizedPath } from './i18n/config';
import { getLocalized } from './utils/localize';
import { getNavItems, ANCHOR_SCROLL_OFFSET } from './utils/nav';
import it from './i18n/locales/it.json';
import en from './i18n/locales/en.json';
import pages from './content/pages.json';
import './styles/base.css';

// Dizionari per le stringhe di "cornice" (footer tagline, ecc.) — il
// contenuto delle pagine invece è tradotto campo per campo in pages.json
// (vedi src/utils/localize.js), non passa da qui.
const dictionaries = { it, en };

function AppShell() {
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setLocale } = useI18n();
  const { locale, slug } = parseLocalizedPath(location.pathname);
  const currentPage = pages.find((p) => p.slug === slug);

  // Il locale nell'URL è la fonte di verità per il contenuto (pagine/blocchi,
  // risolto sotto in modo sincrono); il context i18n resta allineato per le
  // stringhe di cornice (t()) usate da componenti che non ricevono la pagina.
  useEffect(() => { setLocale(locale); }, [locale, setLocale]);

  useEffect(() => {
    startLenis();
    return () => stopLenis();
  }, []);

  // Ricalcola i pin GSAP a ogni cambio pagina (non solo al boot: senza
  // questo, i pin di una pagina raggiunta via navigazione client-side dopo
  // la prima userebbero misure non aggiornate). Se l'URL ha un hash (link
  // diretto o ancora "one-page" cliccata da un'altra pagina — vedi
  // src/utils/nav.js), scrolla lì una volta che i pin sono ricalcolati,
  // altrimenti la posizione target sarebbe sbagliata (spacer non ancora
  // dimensionati correttamente).
  useEffect(() => {
    if (!loaded) return;
    ScrollTrigger.refresh();
    if (!location.hash) return;
    const id = location.hash.slice(1);
    requestAnimationFrame(() => {
      const target = document.getElementById(id);
      if (!target) return;
      const lenisInstance = getLenis();
      if (lenisInstance) lenisInstance.scrollTo(target, { offset: ANCHOR_SCROLL_OFFSET });
      else target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [loaded, location.hash, location.pathname]);

  // Comando ⌘K e nav generati da pagine+sezioni — modificale da /admin.html
  // (tab "Pagine"/blocco → "ancora nel menu"), mai a mano qui.
  const navItems = getNavItems(pages, locale).map((item) => ({ id: item.id, label: item.label, href: item.href }));

  return (
    <>
      {!loaded && <Loader onComplete={() => setLoaded(true)} />}
      <PageMeta page={currentPage} locale={locale} />
      <LocalBusinessSchema />
      <TypographySettings />
      <BackgroundSettings />
      <ScrollProgress />
      <CustomCursor />
      <CommandPalette items={navItems} onNavigate={navigate} />
      {/* Visibile solo al focus da tastiera — permette di saltare header/nav
          senza doverli attraversare a ogni pagina (Tab). */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-lg focus:bg-theme-accent focus:text-theme-accent-text focus:px-4 focus:py-2 focus:text-sm focus:font-medium"
      >
        Vai al contenuto
      </a>
      <div style={{ opacity: loaded ? 1 : 0, transition: 'opacity .4s' }}>
        <Header locale={locale} />
        {/* Una Route per pagina per lingua, generata da src/content/pages.json
            — modifica pagine e contenuti da /admin.html (npm run dev), mai
            qui a mano. Lingua di default senza prefisso (/storia), le altre
            prefissate (/en/storia) — standard per SEO multilingua. */}
        <main id="main-content">
          <Routes>
            {pages.flatMap((page) =>
              LOCALES.map((loc) => (
                <Route
                  key={page.id + loc.code}
                  path={localizedPath(page.slug, loc.code)}
                  element={
                    <BlockRenderer
                      sections={page.sections}
                      locale={loc.code}
                      pageTitle={getLocalized(page.title, loc.code) || getLocalized(page.title, DEFAULT_LOCALE)}
                    />
                  }
                />
              ))
            )}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer locale={locale} />
      </div>
      <CookieBanner />
      <GoogleTagManager />
    </>
  );
}

export function App() {
  return (
    <I18nProvider dictionaries={dictionaries} locales={LOCALES} defaultLocale={DEFAULT_LOCALE}>
      <ConsentProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </ConsentProvider>
    </I18nProvider>
  );
}
